import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../supabaseClient';

interface UserMenuProps {
  onSignInClick: () => void;
}

export default function UserMenu({ onSignInClick }: UserMenuProps) {
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    checkUser();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        setUser(session.user);
        loadProfile(session.user.id);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setProfile(null);
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const checkUser = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      
      if (user) {
        setUser(user);
        await loadProfile(user.id);
      }
    } catch (error) {
      console.error('Error checking user:', error);
    }
  };

  const loadProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      
      if (error) throw error;
      if (data) setProfile(data);
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      setShowDropdown(false);
      setUser(null);
      setProfile(null);
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (!user) {
    return (
      <button
        onClick={onSignInClick}
        className="bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white px-4 sm:px-6 py-2 rounded-lg font-bold transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-red-500/30 hover:shadow-red-500/50 border border-red-400/30 text-sm sm:text-base"
      >
        <i className="ri-login-box-line mr-1 sm:mr-2"></i>
        <span className="hidden sm:inline">Sign In</span>
        <span className="sm:hidden">Sign In</span>
      </button>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-2 sm:gap-3 bg-gray-800 hover:bg-gray-700 px-3 sm:px-4 py-2 rounded-lg transition-colors cursor-pointer"
      >
        <img
          src={profile?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.email}`}
          alt="Avatar"
          className="w-8 h-8 rounded-full"
        />
        <span className="text-white font-semibold hidden sm:inline">{profile?.username || 'User'}</span>
        <i className={`ri-arrow-down-s-line text-white transition-transform ${showDropdown ? 'rotate-180' : ''}`}></i>
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-64 bg-gray-800 rounded-lg shadow-xl border border-gray-700 overflow-hidden z-50">
          <div className="p-4 border-b border-gray-700">
            <div className="flex items-center gap-3">
              <img
                src={profile?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.email}`}
                alt="Avatar"
                className="w-12 h-12 rounded-full"
              />
              <div>
                <div className="text-white font-semibold">{profile?.username || 'User'}</div>
                <div className="text-gray-400 text-sm truncate">{profile?.email || user.email}</div>
                {profile?.is_admin && (
                  <div className="text-red-400 text-xs font-bold mt-1 flex items-center gap-1">
                    <i className="ri-shield-star-line"></i>
                    Admin
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="p-2">
            <a
              href="/profile"
              className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-settings-3-line text-lg"></i>
              <span>Profile Settings</span>
            </a>
            {profile?.is_admin && (
              <a
                href="/admin"
                className="flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-gray-700 rounded-lg transition-colors cursor-pointer font-semibold"
              >
                <i className="ri-shield-star-line text-lg"></i>
                <span>Admin Dashboard</span>
              </a>
            )}
            <a
              href="/streams"
              className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-live-line text-lg"></i>
              <span>My Streams</span>
            </a>
            <button
              onClick={() => {
                setShowDropdown(false);
                const event = new CustomEvent('openMonetization');
                window.dispatchEvent(event);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-money-dollar-circle-line text-lg"></i>
              <span>Monetization</span>
            </button>
            <a
              href="/tournaments"
              className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-trophy-line text-lg"></i>
              <span>Tournaments</span>
            </a>
            <a
              href="/clips"
              className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-video-line text-lg"></i>
              <span>My Clips</span>
            </a>
          </div>

          <div className="p-2 border-t border-gray-700">
            <button
              onClick={handleSignOut}
              className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-logout-box-line text-lg"></i>
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
